"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  LABEL,
  SELECT,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  GridEditor,
  Preview,
} from "@/components/tools/developer/latex-table-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildLatexTable,
  type CaptionPosition,
  type ColumnAlign,
  type LatexTableOptions,
  normalizeRows,
  type Placement,
  parseDelimited,
  type RuleStyle,
  suggestAlignment,
} from "@/lib/tool-kit/developer/latex-table";

/** English strings. A translated content file overrides these through `ui`. */
export const LATEX_TABLE_GENERATOR_STRINGS = {
  dataLabel: "Data",
  modes: { paste: "Paste CSV or TSV", grid: "Edit a grid" },
  pasteLabel: "CSV or TSV — the delimiter is detected automatically",
  pastePlaceholder: "Group,Mean,SD\nControl,4.10,0.82",
  loadExample: "Load the example",
  clear: "Clear",
  exampleData: "Group,Mean,SD\nControl,4.10,0.82\nTreatment,5.30,0.91",
  exampleCaption: "Descriptive statistics by group",
  exampleLabel: "tab:descriptives",
  exampleNote: "Scores range from 1 to 7.",
  cellLabel: "Row {row}, column {column}",
  addRow: "Add row",
  removeRow: "Remove last row",
  addColumn: "Add column",
  removeColumn: "Remove last column",
  structureHeading: "Structure",
  headerRow: "First row is a header",
  boldHeader: "Bold header",
  escape: "Escape special characters: & % $ # _ { } ~ ^ \\",
  centering: "Center the table (\\centering)",
  alignmentLabel: "Column alignment",
  columnLabel: "Column {column}",
  aligns: { l: "Left", c: "Center", r: "Right" },
  rulesLabel: "Rules",
  ruleStyles: {
    booktabs: "Booktabs (\\toprule, \\midrule, \\bottomrule)",
    simple: "Horizontal lines (\\hline)",
    grid: "Full grid (\\hline and |)",
    none: "No rules",
  },
  floatHeading: "Float, caption and notes",
  float: "Wrap in a table float with a caption and label",
  placementLabel: "Placement",
  placements: {
    htbp: "htbp — let LaTeX choose (recommended)",
    "!htbp": "!htbp — try harder to keep it close",
    h: "h — here, if it fits",
    t: "t — top of a page",
    b: "b — bottom of a page",
    p: "p — on a page of floats",
    H: "H — exactly here (float package)",
  },
  captionLabel: "Caption",
  labelLabel: "Label, for \\ref",
  labelPlaceholder: "tab:results",
  captionPositionLabel: "Caption position",
  captionPositions: { above: "Above the table", below: "Below the table" },
  notesLabel: "Table notes — one per line (threeparttable)",
  notePrefix: "Begin the first note with an italic Note.",
  results: "LaTeX code",
  summary: "{rows} by {columns}",
  rowCount: "one: {count} row|other: {count} rows",
  columnCount: "one: {count} column|other: {count} columns",
  codeLabel: "LaTeX",
  copyCode: "Copy LaTeX",
  copied: "Copied!",
  packagesHeading: "Add to your preamble",
  noPackages: "No extra packages needed.",
  copyPackages: "Copy preamble lines",
  previewHeading: "Preview",
  previewHint:
    "An approximation of the typeset table, with cells shown as typed.",
  notePrefixPreview: "Note.",
  emptyPrompt:
    "Paste data or fill in the grid to generate the table — or load the example.",
} as const;

export type LatexTableStrings = typeof LATEX_TABLE_GENERATOR_STRINGS;
type Mode = "paste" | "grid";

const MODES: readonly Mode[] = ["paste", "grid"];
const ALIGNS: readonly ColumnAlign[] = ["l", "c", "r"];
const RULE_STYLES: readonly RuleStyle[] = [
  "booktabs",
  "simple",
  "grid",
  "none",
];
const PLACEMENTS: readonly Placement[] = [
  "htbp",
  "!htbp",
  "h",
  "t",
  "b",
  "p",
  "H",
];
const CAPTION_POSITIONS: readonly CaptionPosition[] = ["above", "below"];
const RE_LINE = /\r?\n/;

function emptyGrid(): string[][] {
  return Array.from({ length: 3 }, () => ["", "", ""]);
}

function toTsv(rows: string[][]): string {
  return rows.map((row) => row.join("\t")).join("\n");
}

function hasContent(rows: string[][]): boolean {
  return rows.some((row) => row.some((cell) => cell.trim() !== ""));
}

export default function LatexTableGeneratorWidget() {
  const strings = useToolUi(LATEX_TABLE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("paste");
  const [pasted, setPasted] = useState("");
  const [grid, setGrid] = useState<string[][]>(emptyGrid);
  const [headerRow, setHeaderRow] = useState(true);
  const [boldHeader, setBoldHeader] = useState(false);
  const [escapeChars, setEscapeChars] = useState(true);
  const [centering, setCentering] = useState(true);
  const [alignOverrides, setAlignOverrides] = useState<
    Record<number, ColumnAlign>
  >({});
  const [rules, setRules] = useState<RuleStyle>("booktabs");
  const [float, setFloat] = useState(true);
  const [placement, setPlacement] = useState<Placement>("htbp");
  const [caption, setCaption] = useState("");
  const [label, setLabel] = useState("");
  const [captionPosition, setCaptionPosition] =
    useState<CaptionPosition>("above");
  const [notesText, setNotesText] = useState("");
  const [notePrefix, setNotePrefix] = useState(false);

  const rows = mode === "paste" ? parseDelimited(pasted) : grid;
  const width = normalizeRows(rows)[0]?.length ?? 0;
  const suggested = suggestAlignment(rows, headerRow);
  const align = suggested.map(
    (value, column) => alignOverrides[column] ?? value
  );
  const options: LatexTableOptions = {
    align,
    rules,
    headerRow,
    boldHeader,
    escape: escapeChars,
    float,
    placement,
    centering,
    caption,
    label,
    captionPosition,
    notes: notesText.split(RE_LINE),
    notePrefix,
  };
  const ready = hasContent(rows);
  const table = ready ? buildLatexTable(rows, options) : null;

  function switchMode(next: Mode) {
    if (next === "grid" && hasContent(rows)) {
      setGrid(normalizeRows(rows));
    } else if (next === "paste" && hasContent(grid)) {
      setPasted(toTsv(grid));
    }
    setMode(next);
  }

  function loadExample() {
    setMode("paste");
    setPasted(strings.exampleData);
    setCaption(strings.exampleCaption);
    setLabel(strings.exampleLabel);
    setNotesText(strings.exampleNote);
    setAlignOverrides({});
  }

  function clearAll() {
    setPasted("");
    setGrid(emptyGrid());
    setCaption("");
    setLabel("");
    setNotesText("");
    setAlignOverrides({});
  }

  return (
    <section className="space-y-6">
      <PillGroup
        label={strings.dataLabel}
        name="ltab-mode"
        onChange={switchMode}
        options={MODES.map((value) => ({ value, label: strings.modes[value] }))}
        value={mode}
      />
      {mode === "paste" ? (
        <TextAreaField
          id="ltab-paste"
          label={strings.pasteLabel}
          onChange={setPasted}
          placeholder={strings.pastePlaceholder}
          rows={7}
          value={pasted}
        />
      ) : (
        <GridEditor grid={grid} onChange={setGrid} strings={strings} />
      )}
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_SMALL} onClick={loadExample} type="button">
          {strings.loadExample}
        </button>
        <button className={BUTTON_SMALL} onClick={clearAll} type="button">
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.structureHeading}>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={headerRow}
            label={strings.headerRow}
            onChange={setHeaderRow}
          />
          <Checkbox
            checked={boldHeader}
            label={strings.boldHeader}
            onChange={setBoldHeader}
          />
          <Checkbox
            checked={escapeChars}
            label={strings.escape}
            onChange={setEscapeChars}
          />
        </div>
        <SelectField
          id="ltab-rules"
          label={strings.rulesLabel}
          onChange={setRules}
          options={RULE_STYLES.map((value) => ({
            value,
            label: strings.ruleStyles[value],
          }))}
          value={rules}
        />
        {width > 0 ? (
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.alignmentLabel}</legend>
            <div className="flex flex-wrap gap-3">
              {align.map((value, column) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                <label className="flex items-center gap-2 text-sm" key={column}>
                  <span className={HINT}>
                    {t(strings.columnLabel, { column: column + 1 })}
                  </span>
                  <select
                    className={`${SELECT} w-auto`}
                    onChange={(event) =>
                      setAlignOverrides((prev) => ({
                        ...prev,
                        [column]: event.target.value as ColumnAlign,
                      }))
                    }
                    value={value}
                  >
                    {ALIGNS.map((option) => (
                      <option key={option} value={option}>
                        {strings.aligns[option]}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
      </Panel>

      <Panel title={strings.floatHeading}>
        <Checkbox checked={float} label={strings.float} onChange={setFloat} />
        {float ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="ltab-caption"
              label={strings.captionLabel}
              onChange={setCaption}
              value={caption}
            />
            <TextField
              id="ltab-label"
              label={strings.labelLabel}
              mono
              onChange={setLabel}
              placeholder={strings.labelPlaceholder}
              value={label}
            />
            <SelectField
              id="ltab-placement"
              label={strings.placementLabel}
              onChange={setPlacement}
              options={PLACEMENTS.map((value) => ({
                value,
                label: strings.placements[value],
              }))}
              value={placement}
            />
            <SelectField
              id="ltab-caption-position"
              label={strings.captionPositionLabel}
              onChange={setCaptionPosition}
              options={CAPTION_POSITIONS.map((value) => ({
                value,
                label: strings.captionPositions[value],
              }))}
              value={captionPosition}
            />
            <div className="self-end">
              <Checkbox
                checked={centering}
                label={strings.centering}
                onChange={setCentering}
              />
            </div>
          </div>
        ) : null}
        <TextAreaField
          id="ltab-notes"
          label={strings.notesLabel}
          onChange={setNotesText}
          prose
          rows={3}
          value={notesText}
        />
        <Checkbox
          checked={notePrefix}
          label={strings.notePrefix}
          onChange={setNotePrefix}
        />
      </Panel>

      <div aria-live="polite" className="space-y-4">
        {table === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <p className={HINT}>
              {t(strings.summary, {
                rows: tPlural(strings.rowCount, locale, rows.length, {
                  count: fmt(rows.length),
                }).trim(),
                columns: tPlural(strings.columnCount, locale, width, {
                  count: fmt(width),
                }).trim(),
              })}
            </p>
            <div className="space-y-2">
              <label className={LABEL} htmlFor="ltab-output">
                {strings.codeLabel}
              </label>
              <textarea
                className={TEXTAREA}
                id="ltab-output"
                readOnly
                rows={Math.min(24, table.code.split("\n").length + 1)}
                spellCheck={false}
                value={table.code}
              />
            </div>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyCode}
              text={table.code}
            />
            <h3 className={WIDGET_H3}>{strings.packagesHeading}</h3>
            {table.packages.length > 0 ? (
              <div className="space-y-2">
                <pre className={`${TEXTAREA} min-h-0`}>
                  {table.packages.join("\n")}
                </pre>
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copyPackages}
                  small
                  text={table.packages.join("\n")}
                />
              </div>
            ) : (
              <Notice>{strings.noPackages}</Notice>
            )}
            <h3 className={WIDGET_H3}>{strings.previewHeading}</h3>
            <p className={HINT}>{strings.previewHint}</p>
            <Preview options={options} rows={rows} strings={strings} />
          </>
        )}
      </div>
    </section>
  );
}
